import { X, ArrowUpRight, Plus, Trash2, Search, Download, FileText, FileSpreadsheet, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { useState, useEffect, useMemo } from "react";

export function Page({ eyebrow, title, subtitle, action }) {
  return (
    <div className="page-heading">
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

export function Panel({ title, subtitle, action, children, className = "" }) {
  return (
    <section className={`panel ${className}`}>
      {(title || action) && (
        <div className="panel-head">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Field({ label, testid, hint, ...props }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input data-testid={testid} {...props} />
      {hint && <small className="hint">{hint}</small>}
    </label>
  );
}

// Türkçe para biçimiyle tutar girişi: "1234,5" → "1.234,50"
export function MoneyInput({ value, onChange, testid, className, style, placeholder = "0,00", decimals = 2, ...props }) {
  const formatMoney = (v) => {
    if (v === "" || v === null || v === undefined) return "";
    const n = Number(v);
    if (isNaN(n)) return "";
    return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: decimals });
  };
  const [text, setText] = useState(() => formatMoney(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(formatMoney(value)); }, [value, focused]); // eslint-disable-line

  const handleChange = (e) => {
    let raw = e.target.value.replace(/[^\d,]/g, "");
    const firstComma = raw.indexOf(",");
    if (firstComma !== -1) {
      raw = raw.slice(0, firstComma + 1) + raw.slice(firstComma + 1).replace(/,/g, "");
    }
    const hasComma = raw.includes(",");
    let [intPart = "", decPart = ""] = raw.split(",");
    intPart = intPart.replace(/^0+(?=\d)/, "");
    decPart = decPart.slice(0, decimals);
    const intFmt = intPart ? Number(intPart).toLocaleString("tr-TR") : (hasComma ? "0" : "");
    const display = hasComma ? `${intFmt},${decPart}` : intFmt;
    setText(display);
    const numeric = intPart === "" && !hasComma ? "" : String(Number(intPart + "." + decPart));
    onChange?.({ target: { value: numeric } });
  };

  return (
    <input type="text" inputMode="decimal" autoComplete="off"
           data-testid={testid} className={className} style={style}
           placeholder={placeholder} value={text}
           onChange={handleChange}
           onFocus={(e) => { setFocused(true); e.target.select(); }}
           onBlur={() => setFocused(false)}
           {...props} />
  );
}

export function MoneyField({ label, testid, hint, value, onChange, ...props }) {
  return (
    <label className="field">
      <span>{label}</span>
      <MoneyInput testid={testid} value={value} onChange={onChange} {...props} />
      {hint && <small className="hint">{hint}</small>}
    </label>
  );
}

export function SelectField({ label, testid, children, ...props }) {
  return (
    <label className="field">
      <span>{label}</span>
      <select data-testid={testid} {...props}>{children}</select>
    </label>
  );
}

export function TextArea({ label, testid, ...props }) {
  return (
    <label className="field">
      <span>{label}</span>
      <textarea data-testid={testid} {...props} />
    </label>
  );
}

export function Modal({ title, subtitle, onClose, children, size = "md", testid, eyebrow }) {
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div data-testid={testid} className={`modal modal-${size}`}>
        <div className="modal-head">
          <div>
            <div className="eyebrow">{eyebrow || (["invoice-view", "statement-modal", "tx-detail-modal", "cheque-detail-modal"].includes(testid) || /detay|ekstre|bilgiler/i.test(String(title || "")) ? "KAYIT DETAYI" : /ayar/i.test(String(title || "")) ? "AYARLAR" : /rapor/i.test(String(title || "")) ? "RAPOR" : /düzenle|güncellendi/i.test(String(title || "")) ? "DÜZENLEME" : "YENİ KAYIT")}</div>
            <h2>{title}</h2>
            {subtitle && <p className="modal-sub">{subtitle}</p>}
          </div>
          <button data-testid="modal-close-button" type="button" className="icon-button" onClick={onClose}>
            <X size={19} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ModalActions({ onCancel, submitLabel = "Kaydet", submitTestId = "modal-submit-button" }) {
  return (
    <div className="modal-actions">
      <button data-testid="modal-cancel-button" type="button" className="secondary-button" onClick={onCancel}>
        Vazgeç
      </button>
      <button data-testid={submitTestId} className="primary-button" type="submit">
        {submitLabel} <ArrowUpRight size={16} />
      </button>
    </div>
  );
}

// Sütuna tıklayınca sıralama: aynı sütuna tekrar tıklayınca yön değişir
const trCmp = new Intl.Collator("tr", { numeric: true, sensitivity: "base" });
export function useSort(rows, accessors, initial = null) {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => {
    if (!sort || !accessors[sort.key]) return rows;
    const get = accessors[sort.key];
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const x = get(a), y = get(b);
      if (typeof x === "number" && typeof y === "number") return (x - y) * dir;
      return trCmp.compare(String(x ?? ""), String(y ?? "")) * dir;
    });
  }, [rows, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (key) => setSort((s) => (s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  return { sorted, sort, toggle };
}

export function SortTh({ label, k, s, align, testid }) {
  const active = s.sort?.key === k;
  const Icon = !active ? ArrowUpDown : s.sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`sortable ${align === "right" ? "amount" : ""} ${active ? "sorted" : ""}`} data-testid={testid || `sort-${k}`}
        onClick={() => s.toggle(k)} title="Sırala">
      <span className="sort-label">{label} <Icon size={12} /></span>
    </th>
  );
}

export const PAGE_SIZE = 15;

export function usePagination(rows, perPage = PAGE_SIZE) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [rows, perPage]);
  const pageCount = Math.max(1, Math.ceil(rows.length / perPage));
  const current = Math.min(page, pageCount);
  return {
    page: current,
    setPage,
    pageCount,
    total: rows.length,
    slice: rows.slice((current - 1) * perPage, current * perPage),
  };
}

export function Pagination({ page, pageCount, total, onChange, testid = "pagination" }) {
  if (pageCount <= 1) return null;
  const go = (p) => onChange(Math.min(Math.max(1, p), pageCount));
  const start = Math.max(1, Math.min(page - 2, pageCount - 4));
  const nums = [];
  for (let i = start; i <= Math.min(pageCount, start + 4); i++) nums.push(i);
  return (
    <div className="pagination" data-testid={testid}>
      <span className="pagination-info" data-testid={`${testid}-info`}>
        Toplam {total} kayıt · Sayfa {page}/{pageCount}
      </span>
      <div className="pagination-btns">
        <button data-testid={`${testid}-prev`} type="button" disabled={page <= 1} onClick={() => go(page - 1)}>‹ Önceki</button>
        {nums.map((n) => (
          <button key={n} data-testid={`${testid}-page-${n}`} type="button"
                  className={n === page ? "active" : ""} onClick={() => go(n)}>{n}</button>
        ))}
        <button data-testid={`${testid}-next`} type="button" disabled={page >= pageCount} onClick={() => go(page + 1)}>Sonraki ›</button>
      </div>
    </div>
  );
}

export function Table({ columns, rows, empty, renderRow, testid, paginate = true }) {
  const accessors = Object.fromEntries(columns.map((c, i) => [i, c.sort]).filter(([, f]) => f));
  const s = useSort(rows, accessors);
  rows = s.sorted;
  const pg = usePagination(rows);
  const shown = paginate ? pg.slice : rows;
  return (
    <div className="table-wrap" data-testid={testid}>
      <table>
        <thead>
          <tr>{columns.map((c, i) => (c.sort
            ? <SortTh key={i} label={c.label} k={i} s={s} align={c.align} testid={`${testid || "table"}-sort-${c.key || i}`} />
            : <th key={i} className={c.align === "right" ? "amount" : ""}>{c.label}</th>))}</tr>
        </thead>
        <tbody>
          {shown.length ? shown.map(renderRow) : (
            <tr><td colSpan={columns.length} className="empty">{empty}</td></tr>
          )}
        </tbody>
      </table>
      {paginate && (
        <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                    onChange={pg.setPage} testid={`${testid || "table"}-pagination`} />
      )}
    </div>
  );
}

export function Badge({ tone = "neutral", children }) {
  return <span className={`badge tone-${tone}`}>{children}</span>;
}

export function PrimaryButton({ onClick, children, testid, icon: Icon = Plus, type = "button" }) {
  return (
    <button data-testid={testid} className="primary-button" type={type} onClick={onClick}>
      <Icon size={17} /> {children}
    </button>
  );
}

export function IconButton({ onClick, testid, title, tone = "muted", children }) {
  return (
    <button data-testid={testid} title={title} onClick={onClick} className={`row-icon tone-${tone}`} type="button">
      {children}
    </button>
  );
}

export function DeleteButton({ onClick, testid, disabled }) {
  return (
    <button data-testid={testid} type="button" onClick={onClick} disabled={disabled}
            className="row-icon tone-danger" title={disabled ? "Silme yetkiniz yok" : "Sil"}>
      <Trash2 size={15} />
    </button>
  );
}

export function SearchInput({ value, onChange, testid, placeholder = "Ara..." }) {
  return (
    <div className="search inline-search">
      <Search size={15} />
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    "Ödendi": "success", "Tahsil Edildi": "success", "Portföyde": "pending",
    "Bekliyor": "pending", "Ödenecek": "pending", "Karşılıksız": "danger",
    "Taslak": "neutral", "Gönderildi": "info", "İade": "neutral", "İptal": "danger",
  };
  return <Badge tone={map[status] || "neutral"}>{status}</Badge>;
}

export function ExportMenu({ testid, options }) {
  // options: [{ label, onClick, icon }]
  const [open, setOpen] = useState(false);
  return (
    <div className="export-menu">
      <button data-testid={testid} className="secondary-button" onClick={() => setOpen((o) => !o)} type="button">
        <Download size={14} /> Dışa aktar
      </button>
      {open && (
        <>
          <div className="export-backdrop" onClick={() => setOpen(false)} />
          <div className="export-list">
            {options.map((o, i) => (
              <button key={i} data-testid={`${testid}-${o.key}`} type="button"
                      onClick={() => { setOpen(false); o.onClick(); }}>
                {o.icon} <span>{o.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function BalancePill({ balance }) {
  if (balance === 0 || balance === undefined) return <span className="balance-pill balance-zero">Denk</span>;
  const isDebt = balance > 0;
  return (
    <span className={`balance-pill ${isDebt ? "balance-debit" : "balance-credit"}`}>
      {isDebt ? "Borç" : "Alacak"}
    </span>
  );
}

export { FileText, FileSpreadsheet };

export function TotalsBar({ items, testid = "totals-bar" }) {
  return (
    <div className="totals-bar" data-testid={testid}>
      {items.map((it) => (
        <div key={it.label} className={`totals-item ${it.tone || ""}`}>
          <span>{it.label}</span>
          <strong data-testid={it.testid}>{it.value}</strong>
          {it.hint && <em>{it.hint}</em>}
        </div>
      ))}
    </div>
  );
}

// Telefonda tabloları karta çevirmek için hücrelere başlık etiketi yazar
function labelTables() {
  document.querySelectorAll(".table-wrap table").forEach((table) => {
    const heads = Array.from(table.querySelectorAll("thead th")).map((th) => th.textContent.trim());
    table.querySelectorAll("tbody tr").forEach((tr) => {
      let col = 0;
      Array.from(tr.children).forEach((td) => {
        const span = Number(td.getAttribute("colspan")) || 1;
        if (span > 1) td.classList.add("m-span");
        const label = span > 1 ? "" : heads[col] || "";
        if (td.dataset.label !== label) td.dataset.label = label;
        if (!label && td.querySelector(".check-btn, input[type=checkbox]")) td.classList.add("m-check");
        else if (!label && td.querySelector("button, a")) td.classList.add("m-actions");
        col += span;
      });
    });
  });
}

export function useMobileTableLabels() {
  useEffect(() => {
    let t = null;
    const run = () => { clearTimeout(t); t = setTimeout(labelTables, 60); };
    run();
    const obs = new MutationObserver(run);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => { obs.disconnect(); clearTimeout(t); };
  }, []);
}

// Yalnız yönetici: kasa etkilenir ama günlük raporda görünmez
export function HideDailyToggle({ form, setForm, testid = "hide-daily-toggle" }) {
  if (!window.__SP_ADMIN) return null;
  return (
    <label className="hide-daily-toggle" data-testid={testid}>
      <input type="checkbox" checked={!!form.hide_daily} onChange={(e) => setForm({ ...form, hide_daily: e.target.checked })} />
      <span><strong>Günlük rapora işleme</strong><small>Kasa bakiyesi etkilenir; günlük raporda, günlük toplamlarda ve diğer kullanıcıların hareket listesinde görünmez.</small></span>
    </label>
  );
}

// Fotoğrafı uygulama içinde tam ekran gösterir (data URI yeni sekmede açılmayabilir)
export function PhotoLightbox({ src, onClose, name = "ruhsat.jpg" }) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="photo-lightbox" data-testid="photo-lightbox" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="photo-lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <a href={src} download={name} className="secondary-button" data-testid="photo-lightbox-download">İndir</a>
        <button type="button" className="primary-button" data-testid="photo-lightbox-close" onClick={onClose}>Kapat</button>
      </div>
      <img src={src} alt="Ruhsat" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}
